import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useAuthStore } from "presentation/stores/use-auth-store";
import {
  useVacationPlanByFilter,
  useCreateVacationPlanMutation,
  useDeleteVacationPlanMutation,
  useUpdateVacationPlanMutation,
} from "presentation/hooks/use-query-vacation";
import { Vacation } from "domain/entities/vacations";
import Spinner from "presentation/components/Shared/Spinner";
import VacationBalance from "./VacationBalance";
import VacationSchedulerForm from "./VacationSchedulerForm";
import VacationList from "./VacationList";
import { VacationInputFilter } from "domain/models/vacation-input-filter";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { useEmployeesById } from "presentation/hooks/use-query-employees";

const ScheduleVacation: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { userEmployee, setUserEmployee } = useAuthStore();
  const employeeId = userEmployee?.id;
  const [vacationDaysLastYear, setVacationDaysLastYear] = useState<number>(0);
  const [vacationDaysCurrentYear, setVacationDaysCurrentYear] =
    useState<number>(0);
  const [showCreateSuccessToast, setShowCreateSuccessToast] = useState(false);
  const [showCreateErrorToast, setShowCreateErrorToast] = useState(false);
  const [createErrorMessage, setCreateErrorMessage] = useState("");
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showDeleteErrorToast, setShowDeleteErrorToast] = useState(false);
  const [showUpdateSuccessToast, setShowUpdateSuccessToast] = useState(false);
  const [showUpdateErrorToast, setShowUpdateErrorToast] = useState(false);
  const { data: employeeData, refetch: refetchEmployeeData } = useEmployeesById(userEmployee?.id);

  const [endDateLimit, setEndDateLimit] = useState<Date | undefined>(undefined);

  const filter: VacationInputFilter = {
    employeeId: employeeId ?? "",
    limit: 100
  };

  const { maxVacationDaysNextYear, setMaxVacationDaysNextYear } = useParameterStore();
  const { data: parameters } = useParameterByFilter({
    limit: 1000,
    sortBy: "value",
  } as BaseInputFilter);

  const {
    data: vacationData,
    isLoading,
    isRefetching,
  } = useVacationPlanByFilter(filter);

  useEffect(() => {
    const vacationDaysCurrentYear =
      vacationData?.data?.[0]?.employee?.vacationDaysCurrentYear ??
      userEmployee?.vacationDaysCurrentYear ??
      0;

    const vacationDaysLastYear =
      vacationData?.data?.[0]?.employee?.vacationDaysLastYear ??
      userEmployee?.vacationDaysLastYear ??
      0;

    setVacationDaysCurrentYear(vacationDaysCurrentYear);
    setVacationDaysLastYear(vacationDaysLastYear);
  }, [vacationData, userEmployee]);


  useEffect(() => {
    const employee = employeeData?.data;

    if (!employee) return;

    setUserEmployee(employee);

    setVacationDaysCurrentYear(
      employee.vacationDaysCurrentYear ?? 0
    );

    setVacationDaysLastYear(
      employee.vacationDaysLastYear ?? 0
    );
  }, [employeeData, setUserEmployee]);




  const { mutate: createVacation, isPending: isCreateVacationPending } = useCreateVacationPlanMutation();
  const { mutate: deleteVacation, isPending: isDeleteVacationPending } = useDeleteVacationPlanMutation();
  const { mutate: updateVacation, isPending: isUpdateVacationPending } = useUpdateVacationPlanMutation();

  const isMutationPending =
    isCreateVacationPending ||
    isDeleteVacationPending ||
    isUpdateVacationPending;

  useEffect(() => {
    setMaxVacationDaysNextYear(
      parameters?.data?.filter((item) => item.type === "maxVacationDaysNextYear")
    );
  }, [parameters]);

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const handleSchedule = (startDate: Date, endDate: Date, isToUseNextYearDays: boolean = false) => {
    if (!employeeId) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Funcionário não identificado.",
        life: 3000,
      });
      return;
    }

    const payload = {
      startAt: startDate,
      endAt: endDate,
      employee: { id: employeeId },
      isToUseNextYearDays
    };

    createVacation(payload, {
      onSuccess: async () => {
        await refetchEmployeeData();

        setShowCreateSuccessToast(true);
      },
      onError: (error: any) => {
        const regexVacationExists =
          /^Employee with id [0-9a-fA-F-]{36} has already VacationPlan\(s\) for some days requested\.$/;

        const regexNotEnoughDays =
          /^Employee with id [0-9a-fA-F-]{36} has not enough vacation days available\.$/;

        const regexInvalidDate =
          /^VacationPlan for employee with id [0-9a-fA-F-]{36} has not valid days\.$/;

        const errorMessage =
          error?.response?.data?.metadata?.errors?.[0] || "";

        const nextYearToConsider = new Date(startDate).getFullYear() + 1;
        const vacationDaysNextYearToConsider =
          new Date(startDate).getFullYear() < new Date().getFullYear() ?
            userEmployee.vacationDaysNextYearFromLastYear :
            userEmployee.vacationDaysNextYear;

        if (regexNotEnoughDays.test(errorMessage) && !isToUseNextYearDays && new Date(startDate).getMonth() + 1 >= 7) {
          confirmDialog({
            message: (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <i
                  className="pi pi-exclamation-triangle"
                  style={{
                    fontSize: "1.5rem",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                />

                <div style={{ lineHeight: "1.5" }}>
                  <div>
                    Não existem dias de férias suficientes disponíveis. Deseja antecipar
                    férias do próximo ano civil ({nextYearToConsider})?
                  </div>
                  <br />
                  <div>
                    Já antecipou {(vacationDaysNextYearToConsider ?? 0) * -1} dia
                    {((vacationDaysNextYearToConsider ?? 0) * -1) !== 1 ? "s" : ""} do ano {nextYearToConsider}, num máximo possível de{" "}
                    {Number(maxVacationDaysNextYear.at(0)?.value)} dia
                    {Number(maxVacationDaysNextYear.at(0)?.value) !== 1 ? "s" : ""}.
                  </div>
                </div>
              </div>
            ),

            header: "Dias de férias insuficientes",

            acceptLabel: "Sim",
            rejectLabel: "Não",

            accept: () => {
              handleSchedule(startDate, endDate, true);
            },

            reject: () => {
              return;
            },
          });

          return;
        }

        const msg = regexVacationExists.test(errorMessage)
          ? "Já existe um agendamento de férias para o período selecionado. Por favor, escolha outro período ou cancele o agendamento existente."
          : regexInvalidDate.test(errorMessage)
            ? "Não é possível agendar férias no período solicitado."
            : regexNotEnoughDays.test(errorMessage) ?
              "Não existem dias de férias suficientes disponíveis."
              : "Erro ao agendar férias.";

        setCreateErrorMessage(msg);
        setShowCreateErrorToast(true);


      },
    });
  };

  const handleCancel = (vacationPlan: Vacation) => {
    const performDelete = () => {

      if (vacationPlan.status === "A Aprovar") {
        deleteVacation(vacationPlan.id ?? "", {
          onSuccess: async () => {
            await refetchEmployeeData();
            
            setShowDeleteSuccessToast(true);

          },
          onError: () => {
            setShowDeleteErrorToast(true);
          },
        });
      }
      else if (vacationPlan.status === "Aprovado") {
        vacationPlan.status = "Cancelamento Pendente";
        vacationPlan.rejectionReason = undefined;
        updateVacation(vacationPlan, {
          onSuccess: () => {
            setShowUpdateSuccessToast(true);
          },
          onError: () => {
            setShowUpdateErrorToast(true);
          },
        });
      }
      else {
        toast.current?.show({
          severity: "info",
          summary: "Informação",
          detail: `Não é possível cancelar férias já no estado ${vacationPlan.status}`,
          life: 3000,
        });
      }

    };

    confirmAction("Deseja cancelar estas férias?", performDelete);
  };


  useEffect(() => {
    if (!endDateLimit) {
      const currentDate = new Date();
      const endYear = currentDate.getFullYear() + 1;

      const endDateLimit = new Date(endYear, 11, 31);
      setEndDateLimit(endDateLimit);
    }
  }, []);

  const onConfirmSchedule = (startDate: Date, endDate: Date) => {
    confirmAction("Deseja agendar essas férias?", () =>
      handleSchedule(startDate, endDate, false)
    );
  };

  const showSpinner =
    isLoading ||
    isRefetching ||
    isMutationPending;

  useEffect(() => {
    if (!showSpinner && showCreateSuccessToast) {
      toast.current?.show({
        severity: "success",
        summary: "Agendamento",
        detail: "Férias agendadas com sucesso!",
        life: 2000,
      });

      setShowCreateSuccessToast(false);
    }
  }, [showSpinner, showCreateSuccessToast]);

  useEffect(() => {
    if (!showSpinner && showDeleteSuccessToast) {
      toast.current?.show({
        severity: "success",
        summary: "Cancelado",
        detail: "Férias canceladas com sucesso!",
        life: 3000,
      });

      setShowDeleteSuccessToast(false);
    }
  }, [showSpinner, showDeleteSuccessToast]);

  useEffect(() => {
    if (!showSpinner && showUpdateSuccessToast) {
      toast.current?.show({
        severity: "success",
        summary: "Cancelado",
        detail: "Pedido de cancelamento das férias efetuado com sucesso!",
        life: 3000,
      });

      setShowUpdateSuccessToast(false);
    }
  }, [showSpinner, showUpdateSuccessToast]);


  useEffect(() => {
    if (!showSpinner && showCreateErrorToast && createErrorMessage) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: createErrorMessage,
        life: 5000,
      });

      setCreateErrorMessage("");
      setShowCreateErrorToast(false);

    }
  }, [showSpinner, showCreateErrorToast, createErrorMessage]);

  useEffect(() => {
    if (!showSpinner && showDeleteErrorToast) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao cancelar as férias.",
        life: 3000,
      });

      setShowDeleteErrorToast(false);
    }
  }, [showSpinner, showDeleteErrorToast]);

  useEffect(() => {
    if (!showSpinner && showUpdateErrorToast) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao cancelar as férias.",
        life: 3000,
      });

      setShowUpdateErrorToast(false);
    }
  }, [showSpinner, showUpdateErrorToast]);


  return (
    <div
      className="timesheet-grid"
      style={{
        position: "relative",
        minHeight: showSpinner ? "300px" : undefined,
      }}
    >
      {!showSpinner && (
        <>
          <VacationBalance
            lastYearDays={vacationDaysLastYear}
            currentYearDays={vacationDaysCurrentYear}
          />

          <VacationSchedulerForm
            onSchedule={onConfirmSchedule}
            endDateLimit={endDateLimit}
          />

          <VacationList
            vacations={vacationData?.data || []}
            isLoading={isLoading}
            onCancel={handleCancel}
          />

        </>
      )
      }

      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />

      {showSpinner && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
        >
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default ScheduleVacation;
